import {
  ShieldCheck,
  CheckCircle,
  DollarSign,
  Home,
  ThumbsUp,
  Ruler,
  CalendarClock,
  Hammer,
  Users,
  Wrench,
} from 'lucide-react';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import Split from '../../components/Split';
import CTASection from '../../components/CTASection';
import Benefits from '../../components/Benefits';
import ContentBlock from '../../components/ContentBlock';
import ProcessRoadmap from '../../components/ProcessRoadmap';
import FAQ from '../../components/FAQ';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import QuickQuoteForm from '../../components/QuickQuoteForm';
import { generateServiceSchema } from '../../utils/generateServiceSchema';
import { generateWebPageSchema } from '../../utils/generateWebPageSchema';
import { generateFAQSchema } from '../../utils/generateFAQSchema';
import FAQData from '../../data/faq/tile-roof.js';



export default function TileRoofPage() {
  const service = {
    title: 'Tile Roofing',
    image: '/hero/best-tile-roof-services-in-los-angeles.webp',
    description:
      'Tile roofing installation by Modern Build. Timeless, durable, and energy-efficient solutions. Free estimate: (323) 300-4130',
    link: '/services/tile-roof',
  };

  const baseUrl = 'https://modernbuildremodel.com';
  const pageUrl = `${baseUrl}${service.link}`;

  const serviceSchema = generateServiceSchema(service, pageUrl);

  // Default location for static service page
  const defaultLocation = ' in Los Angeles, CA';

  // Replace <IN_CITY_ZIP> placeholders in the FAQ answers
  const mergedFaqs = FAQData.map(faq => ({
    ...faq,
    question: faq.question.replace(/<IN_CITY_ZIP>/g, defaultLocation),
    answer: faq.answer.replace(/<IN_CITY_ZIP>/g, defaultLocation),
  }));

      // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": "Services", "item": `${baseUrl}/services` },
      { "@type": "ListItem", "position": 3, "name": "Tile Roof Services", "item": pageUrl }
    ]
  };

  return (
    <div className="bg-white">
    <>
      <Head>
        <title>Tile Roof Installation in Los Angeles | Free Estimate | Modern Build</title>
        <meta name="title" content="Tile Roofing Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="description"
          content="Tile roofing installation by Modern Build. Timeless, durable, and energy-efficient solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="keywords"
          content="tile roofing Los Angeles, clay roof tiles, concrete roof installation, Spanish tile roof, Modern Build roofing"
        />
        <meta name="author" content="Modern Build Home Remodeling" />
        <link rel="canonical" href="https://modernbuildremodel.com/services/tile-roof" />

        <meta property="og:title" content="Tile Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          property="og:description"
          content="Tile roofing installation by Modern Build. Timeless, durable, and energy-efficient solutions. Free estimate: (323) 300-4130"
        />
        <meta
          property="og:image"
          content="https://modernbuildremodel.com/hero/best-tile-roof-services-in-los-angeles.webp"
        />
        <meta
          property="og:url"
          content="https://modernbuildremodel.com/services/tile-roof"
        />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Tile Roof Installation in Los Angeles | Free Estimate | Modern Build" />
        <meta
          name="twitter:description"
          content="Tile roofing installation by Modern Build. Timeless, durable, and energy-efficient solutions. Free estimate: (323) 300-4130"
        />
        <meta
          name="twitter:image"
          content="https://modernbuildremodel.com/hero/best-tile-roof-services-in-los-angeles.webp"
        />
        <meta name="twitter:site" content="@modernbuildhome" />

        {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'Tile Roof Installation in Los Angeles | Free Estimate | Modern Build',
                description:
                  'Modern Build provides tile roof installation across Los Angeles using premium materials for long-term durability and curb appeal.',
                slug: '/services/tile-roof',
              })
            ),
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        {/* JSON-LD: Service */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />        

        {/* JSON-LD: FAQ */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema({
            service: service.title,
            city: 'Studio City',
            zip: '91604',
            faqs: mergedFaqs,
          }))
        }} />
      </Head>

      <Header />
      <Hero category="tileRoof" page="page" hideButton={true} hasContentH1={false}/>

      <QuickQuoteForm serviceTitle={service.title} />

      <Split
        subtitle="BUILT TO ENDURE"
        title="Premium Tile Roofing Installation in Los Angeles"
        background="white"
        description="Tile roofs offer unmatched longevity, fire resistance, and classic beauty. Whether you prefer clay, concrete, or composite tiles, we deliver expert installation tailored to your home’s design and needs."
        extraText="Our skilled roofers ensure every tile is properly laid, sealed, and ready to handle California’s climate for decades."
        phone="(323) 300-4130"
        image1="/split/tile-roof-installation-los-angeles.webp"
        image2="/split/modern-home-with-clay-roof-tiles.webp"
      />

<Benefits
  title="Why Choose Modern Build for Tile Roofing?"
  subtitle="Unmatched craftsmanship, long-term protection"
  items={[
    {
      icon: <ShieldCheck className="w-8 h-8 text-primary" />,
      title: "Work Performed by Licensed, Bonded & Insured Experts",
      href: "/about-us",
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-primary" />,
      title: "Clay & Concrete Tile Options",
    },
    {
      icon: <Wrench className="w-8 h-8 text-primary" />,
      title: "New Installs & Roof Replacements",
    },
    {
      icon: <Ruler className="w-8 h-8 text-primary" />,
      title: "Precision Layout & Sealing",
    },
    {
      icon: <Hammer className="w-8 h-8 text-primary" />,
      title: "Durability & Weather Resistance",
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary" />,
      title: "Strong ROI & Long Lifespan",
    },
    {
      icon: <ThumbsUp className="w-8 h-8 text-primary" />,
      title: "Classic Mediterranean Curb Appeal",
    },
    {
      icon: <Home className="w-8 h-8 text-primary" />,
      title: "Ideal for California Homes",
    },
  ]}
/>


      <ContentBlock
        subtitle="LONG-LASTING ROOFING SOLUTIONS"
        title="Tile Roofing Designed for Southern California"
        description="Tile roofs aren’t just beautiful, they’re built to perform. With proper installation and maintenance, tile roofing can last over 50 years. It’s an ideal choice for energy efficiency, storm resilience, and aesthetic value."
        extraText="We help you choose the right material and color for your home style, then execute a flawless install with strong warranties included."
        buttonText="Get a Free Tile Roofing Estimate"
        background="white"
      />

      <ProcessRoadmap
        title="Our Tile Roofing Process"
        subtitle="From planning to perfection"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "On-Site Assessment",
            description: "We evaluate your current roof, structure, and layout for a tailored tile roof solution.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Tile Selection",
            description: "Choose from premium clay, concrete, or composite tiles in colors that match your home.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Expert Installation",
            description: "Our certified crew installs underlayment, battens, and tiles with precision and care.",
          },
          {
            icon: <CheckCircle className="w-10 h-10 text-primary" />,
            title: "Final Walkthrough",
            description: "We inspect every detail, confirm proper drainage and ventilation, and clean the job site.",
          },
        ]}
      />

      <Split
        subtitle="HIGH-PERFORMANCE ROOFING"
        title="Elevate Your Home With Timeless Tile"
        background="white"
        description="Tile roofing enhances curb appeal, energy efficiency, and protection. Whether you're remodeling or building new, we deliver a stunning roof that performs for decades."
        extraText="We also offer repair services, tile matching, and ongoing maintenance support."
        phone="(323) 300-4130"
        image1="/split/tile-roof-replacement-los-angeles.webp"
        image2="/split/spanish-tile-roofing-contractor.webp"
        reverse={true}
      />

      <CTASection
        title="Book Your Free Tile Roof Consultation"
        subtitle="Professional installation backed by warranty"
        buttonText="Get Started Today"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />

      <FAQ category="tile-roof" />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
    </div>
  );
}
